import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaNewspaper } from 'react-icons/fa';
import TopText from '../components/TopText';

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  background-color: #E3E3E3;
  min-height: 100vh;
  border-radius: 12px;
`;

const ContentContainer = styled.div`
  width: 75%;
  margin: 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  overflow: hidden;
  padding-top: 50px;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 40px 20px;
  }
`;

const BlogPostsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 30px;
  width: 100%;
`;

const BlogPostCard = styled(Link)`
  background: white;
  border: 1px solid rgba(46, 46, 46, 0.2);
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  display: flex;
  gap: 60px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-height: 160px;
  text-decoration: none;
  color: inherit;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
    gap: 20px;
    min-height: auto;
  }
  
  &:hover {
    border-color: rgba(46, 46, 46, 0.4);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PostTitle = styled.h2`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  font-size: 28px;
  margin: 0 0 8px;
  word-break: break-word;
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const PostSubtitle = styled.h3`
  font-family: 'Rubik', sans-serif;
  font-weight: 400;
  color: #6A7D8B;
  font-size: 16px;
  margin: 0 0 16px;
  word-break: break-word;
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const CardFooter = styled.div`
  position: relative;
  margin-top: auto;
  padding-top: 16px;
  width: 100%;
  height: 24px;
  
  @media (max-width: 480px) {
    height: 60px;
    padding-top: 20px;
    margin-top: 16px;
  }
`;

const TeamName = styled.div`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #6A7D8B;
  position: absolute;
  left: 0;
  top: 16px;
  
  @media (max-width: 480px) {
    top: 20px;
  }
`;

const PostDate = styled.div`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #6A7D8B;
  position: absolute;
  right: 0;
  top: 16px;
  
  @media (max-width: 480px) {
    top: 44px;
    left: 0;
    right: auto;
  }
`;

const FeaturedImage = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 30%;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  opacity: 0.3;
  mask-image: linear-gradient(to left, rgba(0,0,0,1), rgba(0,0,0,0));
  -webkit-mask-image: linear-gradient(to left, rgba(0,0,0,1), rgba(0,0,0,0));
  
  @media (max-width: 768px) {
    width: 20%;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    height: 100%;
    opacity: 0.1;
    mask-image: none;
    -webkit-mask-image: none;
  }
`;

function BlogPage() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadBlogPosts = async () => {
      try {
        console.log("Starting to load blog posts");
        
        // Import all markdown files from the blog-posts directory
        const markdownFiles = require.context('../../public/blog-posts', false, /\.md$/);
        const posts = await Promise.all(
          markdownFiles.keys().map(async (filename) => {
            try {
              console.log(`Loading ${filename}...`);
              const response = await fetch(`${process.env.PUBLIC_URL}/blog-posts/${filename.substring(2)}`);
              
              if (!response.ok) {
                console.error(`Failed to load ${filename}: ${response.status} ${response.statusText}`);
                return null;
              }
              
              const text = await response.text();
              console.log(`Received content for ${filename}, length: ${text.length}`);
              
              // Extract front matter
              const frontMatterRegex = /---\s*([\s\S]*?)\s*---/;
              const frontMatterMatch = text.match(frontMatterRegex);
              
              if (frontMatterMatch && frontMatterMatch[1]) {
                const frontMatter = frontMatterMatch[1];
                const metadata = {};
                
                // Parse front matter
                frontMatter.split(/\r?\n/).forEach(line => {
                  const [key, ...valueParts] = line.split(':');
                  if (key && valueParts.length) {
                    metadata[key.trim()] = valueParts.join(':').trim();
                  }
                });
                
                // If featured image is not specified, set a default image or null
                if (!metadata.featuredImage) {
                  metadata.featuredImage = null;
                } else {
                  metadata.featuredImage = `${process.env.PUBLIC_URL}/images/blog-post-features/${metadata.featuredImage}`;
                }
                
                // Set default visibility to true if not specified
                metadata.visible = metadata.visible === undefined ? true : metadata.visible.toLowerCase() === 'true';
                
                return {
                  id: filename.substring(2).replace('.md', ''),
                  ...metadata,
                  content: text.replace(frontMatterRegex, '')
                };
              }
              return null;
            } catch (err) {
              console.error(`Error processing ${filename}:`, err);
              return null;
            }
          })
        );
        
        // Filter out null posts and sort by date
        const validPosts = posts.filter(post => post !== null && post.visible);
        validPosts.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        console.log("Valid posts found:", validPosts.length);
        setBlogPosts(validPosts);
      } catch (error) {
        console.error('Error loading blog posts:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadBlogPosts();
  }, []);

  // Parse date string to ensure consistent handling
  const parseDate = (dateString) => {
    // Try to handle different date formats consistently
    let dateObj;
    
    // Check if we have a date in MM/DD/YYYY format
    const usFormatMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usFormatMatch) {
      const [, month, day, year] = usFormatMatch;
      // Use ISO format for consistent parsing (YYYY-MM-DD)
      dateObj = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
    } else {
      // If we have YYYY-MM-DD format or other format, use standard parsing
      // but force UTC interpretation by appending Z or using Date.UTC
      if (dateString.includes('T')) {
        // If it has a time component already
        dateObj = new Date(dateString);
      } else {
        // If it's just a date without time
        dateObj = new Date(`${dateString}T00:00:00.000Z`);
      }
    }
    
    // Verify we got a valid date
    if (isNaN(dateObj.getTime())) {
      console.error('Failed to parse date:', dateString);
      return null;
    }
    
    return dateObj;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    try {
      const dateObj = parseDate(dateString);
      if (!dateObj) return 'Invalid date';
      
      return dateObj.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Blog | Tempo Stock</title>
        <meta name="description" content="Stay updated with the latest news, tips, and insights about royalty-free music from Tempo Stock." />
      </Helmet>
      <ContentContainer>
        <TopText Icon={FaNewspaper} text="Blog" />
        {loading ? (
          <div>Loading blog posts...</div>
        ) : error ? (
          <div>Error loading blog posts: {error}</div>
        ) : blogPosts.length === 0 ? (
          <div>
            <p>No blog posts found. Please check the console for debugging information.</p>
            <p>Looking for blog posts in: /blog-posts/</p>
          </div>
        ) : (
          <BlogPostsGrid>
            {blogPosts.map((post) => (
              <BlogPostCard key={post.id} to={`/blog/${post.id}`}>
                {post.featuredImage && (
                  <FeaturedImage src={post.featuredImage} />
                )}
                <CardContent>
                  <CardHeader>
                    <div>
                      <PostTitle>{post.title}</PostTitle>
                      <PostSubtitle>{post.subtitle}</PostSubtitle>
                    </div>
                  </CardHeader>
                  <CardFooter>
                    <TeamName>{post.author}</TeamName>
                    <PostDate>{formatDate(post.date)}</PostDate>
                  </CardFooter>
                </CardContent>
              </BlogPostCard>
            ))}
          </BlogPostsGrid>
        )}
      </ContentContainer>
    </PageContainer>
  );
}

export default BlogPage; 